import { ColumnsType } from 'antd/lib/table';
import DefaultModal from 'components/common/DefaultModal';
import Table from 'components/Table';
import { MobileTableType } from 'components/Table/types';
import { formatDateTimeForDisplay } from 'constants/days';
import useReactiveQueryTable from 'hooks/useReactiveQueryTable';
import { isFunction } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { GET_INVOICES } from 'services/graphql/queries/invoice.query';
import { OrderEnum } from 'types/common';
import { IInvoice } from 'types/invoice';

import InvoiceDetail from './InvoiceDetail';
import { IInvoiceTableProps } from './types';

const ClientInvoiceTable: React.FC<IInvoiceTableProps> = ({ companyData, setRefetchInvoice, refetchUserData }) => {
  const invoiceColumn: ColumnsType<any> = [
    {
      title: 'Invoice #',
      dataIndex: 'reference',
      render: (reference: string) => <span className='gx-font-weight-medium'>#{reference}</span>,
      // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
    },
    {
      title: 'Issue Date',
      dataIndex: 'createdAt',
      // render: (date: string ) => <div>Appointments: {date}</div>,
      sorter: true,
    },
    {
      title: 'Appointments',
      dataIndex: 'numAppointments',
      render: (numAppointments: number) => <span>{numAppointments}</span>,
      sorter: true,
    },
    {
      title: 'Amount',
      dataIndex: 'total',
      render: (total: number) => <span style={{ color: '#038fde' }}>${total}</span>,
      sorter: true,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      render: (balance: number) => (
        <span style={{ color: !balance ? '#12995E' : '#de9203' }}>{!balance ? 'Paid' : `$${balance}`}</span>
      ),
      sorter: true,
    },
  ];

  const [activeInvoice, setActiveInvoice] = useState<IInvoice>();

  const dataMapper = (invoice: IInvoice) => ({
    key: invoice.id,
    reference: invoice.invoiceId,
    createdAt: moment(invoice.createdAt).format(formatDateTimeForDisplay('|')),
    numAppointments: invoice.invoiceDetails.length,
    total: invoice.total,
    balance: invoice.balance,
    invoice,
  });

  const tableProps = useReactiveQueryTable({
    graphQLQuery: GET_INVOICES,
    columns: invoiceColumn,
    dataMapper,
    mapQueryToVariables: () => ({
      target: companyData?.id,
    }),
    queryProps: {
      variables: {
        order: {
          createdAt: OrderEnum.ASC,
        },
      },
    },
  });

  useEffect(() => {
    if (tableProps.refetch && isFunction(tableProps.refetch)) setRefetchInvoice?.(() => tableProps.refetch);
  }, [tableProps.refetch]);

  useEffect(() => {
    if (activeInvoice) {
      setActiveInvoice((c) => tableProps?.dataSource?.find((source) => source.invoice.id === c?.id)?.invoice || c);
    }
  }, [tableProps]);

  const onRowClick = (invoice: IInvoice) => {
    setActiveInvoice(invoice);
  };

  const onCloseModal = () => {
    setActiveInvoice(undefined);
  };

  const refetchData = () => {
    tableProps.refetch?.();
    refetchUserData?.();
  };

  return (
    <>
      <DefaultModal
        props={{
          visible: !!activeInvoice,
          title: activeInvoice?.invoiceId || 'Unknown Ref Code',
          destroyOnClose: true,
          onCancel: onCloseModal,
          width: '90%',
          style: {
            maxWidth: 900,
          },
          bodyStyle: { padding: 0 },
        }}>
        <InvoiceDetail refetch={refetchData} invoiceTargetCompany={companyData} invoice={activeInvoice} />
      </DefaultModal>
      <Table
        {...tableProps}
        onRow={(record) => ({
          onClick: () => {
            onRowClick(record.invoice as IInvoice);
          },
        })}
        widthThreshold={576}
        mobileDesignType={MobileTableType.RESPONSIVE_TABLE}
        responsiveFullBordered
      />
    </>
  );
};

export default React.memo(ClientInvoiceTable);
